import React, { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Project } from "@/types";
import { CategoryType } from "@/types/category";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import ProjectFilter from "./ProjectFilter";
import CategoryCards from "./CategoryCards";
import CategoryChart from "./CategoryChart";
import CategoryDetailDesktopPage from "./CategoryDetailDesktopPage";
import CategoryDetailMobilePage from "./CategoryDetailMobilePage";
import { useCategoryData } from "@/hooks/useCategoryData";
import { useIsMobile } from "@/hooks/use-mobile";

interface CategoryOverviewProps {
  projects: Project[];
  projectToBatchIds: Record<string, string[]>;
  batchTitles: Record<string, string>;
  customerColor: string;
}

const CategoryOverview: React.FC<CategoryOverviewProps> = ({
  projects,
  projectToBatchIds,
  batchTitles,
  customerColor,
}) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [viewType, setViewType] = useState<"cards" | "chart">("cards");
  const [selectedCategory, setSelectedCategory] = useState<
    CategoryType | undefined
  >();
  const [selectedProjectId, setSelectedProjectId] = useState<
    string | undefined
  >();
  const [selectedCategoryForDetail, setSelectedCategoryForDetail] = useState<
    CategoryType | undefined
  >();

  // Get filtered category data, measurements, and filtered projects
  const { categoryData, projectsMeasurements, filteredProjects } =
    useCategoryData({
      projects,
      projectToBatchIds,
      batchTitles,
      selectedCategory,
      selectedProjectId,
    });

  // Helper function to render HTML content safely
  const renderHTML = (htmlString: string) => {
    return { __html: htmlString };
  };

  return (
    <div>
      <div>
        <h2
          className="text-3xl font-semibold mb-4"
          dangerouslySetInnerHTML={renderHTML(
            t("categoryOverview.title").replace(
              "{coloredWord}",
              `<span style="color: ${customerColor}">${t(
                "categoryOverview.coloredWord"
              )}</span>`
            )
          )}
        />

        <p
          className="text-gray-600 mb-10 w-full sm:w-1/3"
          dangerouslySetInnerHTML={renderHTML(t("categoryOverview.subtitle"))}
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-10 gap-4">
        <ProjectFilter
          projects={projects}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          selectedProjectId={selectedProjectId}
          onProjectChange={setSelectedProjectId}
        />

        <ToggleGroup
          type="single"
          value={viewType}
          onValueChange={(value) =>
            value && setViewType(value as "cards" | "chart")
          }
          className="border border-gray-200 rounded-md self-start sm:self-auto"
        >
          <ToggleGroupItem
            value="cards"
            className="px-4 py-2"
            style={{
              backgroundColor: viewType === "cards" ? customerColor : "white",
              color: viewType === "cards" ? "white" : "inherit",
            }}
          >
            {t("categoryOverview.viewType.cards")}
          </ToggleGroupItem>
          <ToggleGroupItem
            value="chart"
            className="px-4 py-2"
            style={{
              backgroundColor: viewType === "chart" ? customerColor : "white",
              color: viewType === "chart" ? "white" : "inherit",
            }}
          >
            {t("categoryOverview.viewType.chart")}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="mt-6">
        {viewType === "cards" && (
          <CategoryCards
            projects={projects}
            projectToBatchIds={projectToBatchIds}
            batchTitles={batchTitles}
            selectedCategory={selectedCategory}
            selectedProjectId={selectedProjectId}
            customerColor={customerColor}
            onCategoryClick={setSelectedCategoryForDetail}
          />
        )}

        {viewType === "chart" && (
          <CategoryChart
            projects={projects}
            projectToBatchIds={projectToBatchIds}
            batchTitles={batchTitles}
            selectedCategory={selectedCategory}
            selectedProjectId={selectedProjectId}
          />
        )}
      </div>

      {/* Category Detail Page Modal */}
      {selectedCategoryForDetail &&
        (isMobile ? (
          <CategoryDetailMobilePage
            category={selectedCategoryForDetail}
            projects={filteredProjects}
            categoryData={categoryData}
            projectsMeasurements={projectsMeasurements}
            onClose={() => setSelectedCategoryForDetail(undefined)}
            customerColor={customerColor}
          />
        ) : (
          <CategoryDetailDesktopPage
            category={selectedCategoryForDetail}
            projects={filteredProjects}
            categoryData={categoryData}
            projectsMeasurements={projectsMeasurements}
            onClose={() => setSelectedCategoryForDetail(undefined)}
            onCategoryChange={setSelectedCategoryForDetail}
            customerColor={customerColor}
          />
        ))}
    </div>
  );
};

export default CategoryOverview;
